import { ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_CREATOR, ROLE_EDITOR, ROLE_RECEIVER, ROLE_SUPPORT } from 'Components/constants';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'Config';

/** ***************** */
// THUNKS
/** ***************** */
export const ID_PARAM = 'id';

export interface AuthState {
  user: User | null;
  userEmedia: UserEmedia | null;
  refresh: string;
  auth: string;
  lastAttempt: number;
}

export const initialState: AuthState = {
  user: null,
  userEmedia: null,
  refresh: '',
  auth: '',
  lastAttempt: 0,
};

export const { actions, reducer, name } = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    set: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
    },
    update: (state, action: PayloadAction<Partial<User>>) => {
      const id = typeof action.payload.id === 'string' ? action.payload.id : state.user?.id;
      if (state.user) {
        state.user = {
          ...state.user,
          ...action.payload,
          id: String(id),
        };
      } else {
        console.error('cannot update user info,  since there were no intial values');
      }
    },
    setUserEMedia: (state, action: PayloadAction<UserEmedia>) => {
      state.userEmedia = action.payload;
    },
    logout: () => {
      // this is going to be handled in the root reducer
    },
  },
});

export const slice = (state: RootState) => state[name] || initialState;

export const selectUser = (state: RootState) => {
  return slice(state).user;
};

export const selectEmediaUser = (state: RootState) => {
  return slice(state).userEmedia;
};

export const selectUserToken = (state: RootState) => {
  return slice(state).user?.access_token;
};

export const selectRefreshToken = (state: RootState) => {
  return slice(state).user?.refresh_token;
};

export const selectEmediaUserRole = (state: RootState) => {
  return slice(state).userEmedia?.role || '';
};

export const canCreateEMA = (state: RootState) => {
  return [ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_CREATOR, ROLE_EDITOR].includes(slice(state).userEmedia?.role || '');
};

export const canEditEMAStatus = (state: RootState) => {
  return [ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_CREATOR, ROLE_EDITOR].includes(slice(state).userEmedia?.role || '');
};

export const canEditEMADetails = (state: RootState) => {
  return [ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_CREATOR, ROLE_EDITOR].includes(slice(state).userEmedia?.role || '');
};

export const canEditJobnummer = (state: RootState) => {
  return [ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_EDITOR, ROLE_SUPPORT].includes(slice(state).userEmedia?.role || '');
};

export const canSelectPM = (state: RootState) => {
  return [ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_EDITOR].includes(slice(state).userEmedia?.role || '');
};

export const canAddOutputMedia = (state: RootState) => {
  return [ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_CREATOR, ROLE_EDITOR].includes(slice(state).userEmedia?.role || '');
};

export const canDeleteOutputMedia = (state: RootState) => {
  return [ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_CREATOR, ROLE_EDITOR].includes(slice(state).userEmedia?.role || '');
};

export const canGenerateAssignAssetID = (state: RootState) => {
  return [ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_EDITOR, ROLE_SUPPORT].includes(slice(state).userEmedia?.role || '');
};

export const canUploadFineData = (state: RootState) => {
  return [ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_EDITOR, ROLE_SUPPORT].includes(slice(state).userEmedia?.role || '');
};

export const canRejectFineData = (state: RootState) => {
  return [ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_RECEIVER].includes(slice(state).userEmedia?.role || '');
};

export const canEditNoteFineData = (state: RootState) => {
  return [ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_EDITOR, ROLE_SUPPORT, ROLE_RECEIVER].includes(slice(state).userEmedia?.role || '');
};

export const canDeleteFineData = (state: RootState) => {
  return [ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_EDITOR, ROLE_SUPPORT].includes(slice(state).userEmedia?.role || '');
};
